import React, { useState, useRef, useMemo } from 'react';
import PrimaryModal from '../../UI/Modal/PrimaryModal';
import { PrimaryButton, SecondaryButton } from "../../UI";
import AgGridWrapper from '../../UI/AgGrid';
import { CSVLink } from "react-csv";
import Papa from 'papaparse';
import { useUserContext } from '../../../contexts/UserContext';
import { uploadMetadataFromCSV, updateAdobeUsersMetadata, deleteAdobeUsersMetadataKey } from '../../../utils/APIs';
import { toast } from 'react-toastify';
import { XCircle, CheckCircle, Download, Upload } from 'lucide-react';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import ConfirmModal from '../../UI/Modal/confirmModal';

const AddMetadataModal = ({ isOpen, onClose, users, existingMetadataKeys, refreshUsers }) => {
    const { userData } = useUserContext();
    const { enums, content } = useContentfulContext();
    const addMetaContent = content?.USER_MANAGEMENT_ADD_METADATA_MODAL_CONTENT || {};

    // Step 1: Define metadata keys  |  Step 2: Review & Upload
    const [step, setStep] = useState('define'); // 'define' | 'review'
    const [metadataKeys, setMetadataKeys] = useState([]);
    const [keysToRemove, setKeysToRemove] = useState([]);
    const [keyInput, setKeyInput] = useState('');
    const [reviewRows, setReviewRows] = useState([]);
    const [errors, setErrors] = useState({});   // { email: { field: "error" } }
    const [gridApi, setGridApi] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [showRemovalConfirm, setShowRemovalConfirm] = useState(false);
    const [removedKeysState, setRemovedKeysState] = useState([]);
    const [keyToAdd, setKeyToAdd] = useState('');
    const [showAddConfirm, setShowAddConfirm] = useState(false);
    const [keyConfirmLoading, setKeyConfirmLoading] = useState(false);
    const [keyToDelete, setKeyToDelete] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
    const fileInputRef = useRef(null);

    const handleAddKey = (e) => {
        if ((e.key === 'Enter' || e.type === 'click') && keyInput.trim()) {
            e.preventDefault();
            const newKey = keyInput.trim();
            if (!metadataKeys.includes(newKey) && !existingMetadataKeys.includes(newKey)) {
                setKeyToAdd(newKey);
                setShowAddConfirm(true);
                if (document.activeElement) {
                    document.activeElement.blur();
                }
            } else {
                toast.warning((addMetaContent.messages?.key_exists || 'Key "{key}" already exists.').replace('{key}', newKey));
                setKeyInput('');
            }
        }
    };

    const confirmAddKey = async () => {
        if (!keyToAdd) return;
        setKeyConfirmLoading(true);
        try {
            // Save the new key with empty string value for every existing user in DB
            if (users && users.length > 0) {
                const updates = users
                    .filter(u => u._id || u.id)
                    .map(u => ({
                        id: u._id || u.id,
                        metadata: { [keyToAdd]: '' }
                    }));
                if (updates.length > 0) {
                    await updateAdobeUsersMetadata(
                        userData?.token,
                        userData?.selectedOrganization?.org_token,
                        updates
                    );
                }
            }
            // Refresh user list so export CSV reflects the new column
            if (refreshUsers) await refreshUsers();
            toast.success(`Metadata key "${keyToAdd}" added for all users.`);
        } catch (err) {
            console.error('Failed to save metadata key:', err);
            toast.error(err?.message || 'Failed to save metadata key. Please try again.');
        } finally {
            setKeyToAdd('');
            setKeyInput('');
            setShowAddConfirm(false);
            setKeyConfirmLoading(false);
        }
    };

    const cancelAddKey = () => {
        setKeyToAdd('');
        setShowAddConfirm(false);
    };

    const confirmDeleteKey = async () => {
        if (!keyToDelete) return;
        setDeleteConfirmLoading(true);
        try {
            await deleteAdobeUsersMetadataKey(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                keyToDelete
            );
            if (refreshUsers) await refreshUsers();
            toast.success(
                (addMetaContent.messages?.delete_key_success || 'Metadata key "{key}" deleted from all users.')
                    .replace('{key}', keyToDelete)
            );
        } catch (err) {
            console.error('Failed to delete metadata key:', err);
            toast.error(err?.message || addMetaContent.messages?.delete_key_failed || 'Failed to delete metadata key. Please try again.');
        } finally {
            setKeyToDelete('');
            setShowDeleteConfirm(false);
            setDeleteConfirmLoading(false);
        }
    };

    const cancelDeleteKey = () => {
        setKeyToDelete('');
        setShowDeleteConfirm(false);
    };

    const renderDeleteConfirmDescription = () => {
        const rawTemplate = addMetaContent?.delete_key_confirm?.description ||
            "Are you sure you want to delete the metadata key {key} and all its values from every adobe user? This action cannot be undone.";
        if (rawTemplate.includes('{key}')) {
            const parts = rawTemplate.split('{key}');
            return (
                <span>
                    {parts[0]}
                    <strong className="font-bold text-red-600">"{keyToDelete}"</strong>
                    {parts[1]}
                </span>
            );
        }
        return rawTemplate;
    };

    const renderAddConfirmationDescription = () => {
        const rawTemplate = addMetaContent?.add_confirm?.description || "Are you sure you want to add {key} as new metadata key for all adobe users.";
        if (rawTemplate.includes('{key}')) {
            const parts = rawTemplate.split('{key}');
            return (
                <span>
                    {parts[0]}
                    <strong className="font-bold text-blue-600">"{keyToAdd}"</strong>
                    {parts[1]}
                </span>
            );
        }
        return rawTemplate;
    };

    const removeKey = (key) => {
        setMetadataKeys(prev => prev.filter(k => k !== key));
    };

    // Build all visible metadata columns: existing (not removed) + new
    const allKeys = [...existingMetadataKeys.filter(k => !keysToRemove.includes(k)), ...metadataKeys];

    // Generate CSV for download — all users, existing metadata + new empty columns, NO products
    const buildExportCSV = () => {
        if (!users || !users.length) return [['email', ...allKeys]];
        const rows = users.map(user => {
            const meta = user.metadata instanceof Map
                ? Object.fromEntries(user.metadata)
                : (user.metadata || {});
            return [user.email, ...allKeys.map(k => meta[k] || '')];
        });
        return [['email', ...allKeys], ...rows];
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: function (results) {
                const data = results.data;
                if (!data.length) {
                    toast.error(addMetaContent.messages?.csv_no_data || "No data found in the CSV file.");
                    return;
                }

                const headers = Object.keys(data[0]);
                const detectedMetaKeys = headers.filter(h => h.toLowerCase() !== 'email');

                const rows = data.map(row => {
                    const email = row.email || row.Email || row.EMAIL || '';
                    const metadata = {};
                    detectedMetaKeys.forEach(k => {
                        if (row[k] !== undefined) metadata[k] = row[k];
                    });
                    return { email: email.trim(), metadata };
                }).filter(r => r.email);

                setReviewRows(rows);
                setErrors({});
                setStep('review');
            }
        });

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Review table column defs
    const allReviewKeys = reviewRows.length
        ? Object.keys(reviewRows[0]?.metadata || {})
        : allKeys;

    const reviewColumnDefs = [
        {
            headerName: addMetaContent.table_headers?.status || 'Status',
            field: 'status',
            width: 80,
            sortable: false,
            filter: false,
            resizable: true,
            cellRenderer: (params) => {
                const rowEmail = params.data.email;
                const rowErrors = errors[rowEmail];
                const hasError = rowErrors && Object.keys(rowErrors).length > 0;
                if (!params.data.email) return null;
                const errorMsg = hasError ? Object.values(rowErrors).join(' | ') : '';
                return (
                    <div className="flex items-center justify-center h-full" title={errorMsg}>
                        {hasError
                            ? <XCircle color="#ef4444" size={18} className="cursor-help" />
                            : <CheckCircle color="#10b981" size={18} />
                        }
                    </div>
                );
            }
        },
        {
            headerName: addMetaContent.table_headers?.email || 'Email',
            field: 'email',
            flex: 1,
            minWidth: 200,
            resizable: true,
            filter: true,
            cellStyle: params => {
                if (errors[params.data.email]?.email) {
                    return { border: '1px solid #ef4444', backgroundColor: '#fef2f2' };
                }
                return {};
            }
        },
        ...allReviewKeys.map(key => ({
            headerName: key,
            field: `metadata.${key}`,
            width: 160,
            editable: true,
            resizable: true,
            filter: true,
            valueGetter: params => params.data.metadata?.[key] || '',
            valueSetter: params => {
                const newRows = reviewRows.map(r =>
                    r.email === params.data.email
                        ? { ...r, metadata: { ...r.metadata, [key]: params.newValue } }
                        : r
                );
                setReviewRows(newRows);
                // Clear error on edit
                if (errors[params.data.email]) {
                    setErrors(prev => {
                        const n = { ...prev };
                        delete n[params.data.email];
                        return n;
                    });
                }
                return true;
            },
            cellStyle: params => {
                if (errors[params.data.email]?.metadata) {
                    return { border: '1px solid #ef4444', backgroundColor: '#fef2f2' };
                }
                return {};
            }
        }))
    ];

    const autoSizeStrategy = useMemo(() => ({
        type: "fitGridWidth",
        defaultMinWidth: 100
    }), []);

    const handleSubmit = async () => {
        if (!reviewRows.length) {
            toast.error(addMetaContent.messages?.no_rows_submit || "No rows to submit.");
            return;
        }

        // Client-side validation
        const clientErrors = {};
        reviewRows.forEach(row => {
            if (!row.email || !row.email.includes('@')) {
                clientErrors[row.email || 'unknown'] = { email: addMetaContent.messages?.invalid_email || "Invalid email address." };
            }
        });

        if (Object.keys(clientErrors).length > 0) {
            setErrors(clientErrors);
            if (gridApi) gridApi.redrawRows();
            toast.error(addMetaContent.messages?.fix_highlighted_rows || "Please fix the highlighted rows before submitting.");
            return;
        }

        // Check for removed metadata keys
        const removedKeys = existingMetadataKeys.filter(k => !allReviewKeys.includes(k));
        if (removedKeys.length > 0) {
            setRemovedKeysState(removedKeys);
            setShowRemovalConfirm(true);
            return;
        }

        executeSubmit();
    };

    const executeSubmit = async () => {
        setShowRemovalConfirm(false);
        setActionLoading(true);
        try {
            const res = await uploadMetadataFromCSV(userData?.token, userData?.selectedOrganization?.org_token, reviewRows);
            if (res?.status) {
                if (res?.hasWarning) {
                    toast.warning(res.message || "Metadata updated for available users");
                } else {
                    toast.success(res.message || addMetaContent.messages?.upload_success || "Metadata updated successfully.");
                }
                refreshUsers();
                handleClose();
            } else {
                toast.error(res?.message || addMetaContent.messages?.upload_failed || "Failed to update metadata.");
            }
        } catch (error) {
            console.error("Failed to upload metadata", error);
            if (error?.errors) {
                toast.error(error?.message || addMetaContent.messages?.validation_failed || "Validation failed for some rows.");
                setErrors(error.errors);
                if (gridApi) gridApi.redrawRows();
            } else {
                toast.error(error?.message || addMetaContent.messages?.upload_failed || "Failed to update metadata. Please try again.");
            }
        } finally {
            setActionLoading(false);
        }
    };

    const handleClose = () => {
        setStep('define');
        setMetadataKeys([]);
        setKeysToRemove([]);
        setKeyInput('');
        setReviewRows([]);
        setErrors({});
        setActionLoading(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <PrimaryModal
            hideModal={handleClose}
            title={step === 'define' ? (addMetaContent.define_title || 'Manage User Metadata') : (addMetaContent.review_title || 'Review & Upload Metadata')}
            widthClass="max-w-5xl"
        >
            <div className="p-6">
                {step === 'define' ? (
                    <>
                        {/* Tag-style metadata key input */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {addMetaContent.new_keys_label || "New Metadata Keys"}
                            </label>
                            <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2.5 pt-2.5 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 min-h-[48px]">
                                {metadataKeys.map((key, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                                        {key}
                                        <button
                                            type="button"
                                            onClick={() => removeKey(key)}
                                            className="ml-0.5 text-blue-500 hover:text-red-500"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                ))}
                                <input
                                    type="text"
                                    className="flex-1 min-w-[140px] outline-none bg-transparent border-none p-0 text-sm placeholder-gray-400"
                                    placeholder={metadataKeys.length === 0 ? (addMetaContent.key_input_placeholder_empty || "Type a key and press Enter...") : (addMetaContent.key_input_placeholder_exists || "Add another key...")}
                                    value={keyInput}
                                    onChange={e => setKeyInput(e.target.value)}
                                    onKeyDown={handleAddKey}
                                    disabled={showAddConfirm}
                                />
                            </div>
                            <p className="mt-1.5 text-xs text-gray-500" dangerouslySetInnerHTML={{ __html: addMetaContent.enter_instruction || 'Press <kbd class="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 font-mono text-[11px]">Enter</kbd> to add a metadata key. These will become new columns in the CSV.' }} />

                            {existingMetadataKeys.length > 0 && (
                                <div className="mt-3">
                                    <p className="text-xs font-semibold text-gray-500 tracking-wider mb-1.5">{addMetaContent.existing_keys_label || "Existing Metadata Keys"}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {existingMetadataKeys.map((key, i) => {
                                            const isRemoved = keysToRemove.includes(key);
                                            return (
                                                <span key={i} className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${isRemoved ? 'bg-red-50 text-red-600 border-red-200 line-through' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                    {key}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setKeyToDelete(key);
                                                            setShowDeleteConfirm(true);
                                                        }}
                                                        className="ml-0.5 focus:outline-none text-gray-400 hover:text-red-500"
                                                        title="Delete column from all users"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                    {keysToRemove.length > 0 && (<p className="text-xs text-red-500">
                                        {addMetaContent.remove_warning_msg || "Columns marked for removal will NOT be included in the exported CSV. Uploading the new CSV will then permanently delete them."}
                                    </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Export CSV button */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200 mb-6">
                            <div className="flex items-start gap-2 flex-1">
                                <Download size={16} className="text-slate-500 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold text-slate-800">{addMetaContent.export_section?.title || "Export Users with Updated Metadata Columns"}</p>
                                    <p className="text-xs text-slate-500 mt-0.5">{addMetaContent.export_section?.description || "Downloads a CSV with all existing users, their current metadata values, and any new columns you defined above (with empty values)."}</p>
                                </div>
                            </div>
                            <CSVLink
                                data={buildExportCSV()}
                                filename="adobe_users_metadata.csv"
                                className="no-underline shrink-0"
                            >
                                <SecondaryButton size="sm" className="whitespace-nowrap">
                                    {addMetaContent.export_section?.btn_text || "Export CSV"}
                                </SecondaryButton>
                            </CSVLink>
                        </div>

                        {/* Upload CSV */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                            <div className="flex items-start gap-2 flex-1">
                                <Upload size={16} className="text-blue-500 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold text-blue-800">{addMetaContent.upload_section?.title || "Upload Filled CSV"}</p>
                                    <p className="text-xs text-blue-600 mt-0.5">{addMetaContent.upload_section?.description || "After filling in the exported CSV, upload it here to review before saving."}</p>
                                </div>
                            </div>
                            <PrimaryButton
                                size="sm"
                                handleClick={() => fileInputRef.current.click()}
                                className="whitespace-nowrap"
                            >
                                {addMetaContent.upload_section?.btn_text || "Upload CSV"}
                            </PrimaryButton>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept=".csv"
                                onChange={handleFileUpload}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        {/* Review step */}
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">{(addMetaContent.review_section?.loaded_text || "{count} row(s) loaded for review").replace('{count}', reviewRows.length)}</p>
                                <p className="text-xs text-gray-500">{addMetaContent.review_section?.description || "Review the table below. Double-click cells to edit before saving."}</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { setStep('define'); setErrors({}); }}
                                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                                >
                                    {addMetaContent.review_section?.back_btn || "← Back"}
                                </button>
                                <PrimaryButton
                                    size="sm"
                                    handleClick={handleSubmit}
                                    disable={actionLoading}
                                    className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                                >
                                    {actionLoading ? (addMetaContent.review_section?.saving_btn || 'Saving...') : (addMetaContent.review_section?.save_btn || 'Confirm & Save')}
                                </PrimaryButton>
                            </div>
                        </div>

                        <AgGridWrapper
                            tableId="metadata-review-table"
                            columnDefs={reviewColumnDefs}
                            rowData={reviewRows}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                            }}
                            onGridReady={params => setGridApi(params.api)}
                            suppressRowClickSelection={true}
                            autoSizeStrategy={autoSizeStrategy}
                            paginationPageSize={content.MANAGE_EXPERIENCE_CONTENT.default_page_size}
                            paginationPageSizeSelector={enums.PAGINATION_PAGE_SIZE_SELECTOR}
                        />
                    </>
                )}
            </div>
            {showRemovalConfirm && (
                <ConfirmModal
                    title={addMetaContent.removal_confirm?.title || "Warning: Metadata Removal"}
                    description={(addMetaContent.removal_confirm?.description || "{keys} meta data and values will be removed from all users. Are you sure you want to proceed?").replace('{keys}', removedKeysState.join(', '))}
                    ConfirmText={addMetaContent.removal_confirm?.confirm_text || "Confirm & Save"}
                    handleConfirmation={executeSubmit}
                    hideModal={() => setShowRemovalConfirm(false)}
                />
            )}
            {showAddConfirm && (
                <ConfirmModal
                    title={addMetaContent?.add_confirm?.title || "Confirm Metadata Key Addition"}
                    description={renderAddConfirmationDescription()}
                    ConfirmText={addMetaContent.add_confirm?.confirm_text || "Confirm"}
                    handleConfirmation={confirmAddKey}
                    hideModal={cancelAddKey}
                    loading={keyConfirmLoading}
                />
            )}
            {showDeleteConfirm && (
                <ConfirmModal
                    title={addMetaContent?.delete_key_confirm?.title || "Delete Metadata Key"}
                    description={renderDeleteConfirmDescription()}
                    ConfirmText={addMetaContent?.delete_key_confirm?.confirm_text || "Delete"}
                    handleConfirmation={confirmDeleteKey}
                    hideModal={cancelDeleteKey}
                    loading={deleteConfirmLoading}
                />
            )}
        </PrimaryModal>
    );
};

export default AddMetadataModal;
